import type { Asset, GatewayEnrollment, InventoryRunSummary } from "../api/rest";
import type { GatewayStatus } from "../stores/telemetryStore";

/**
 * ADR 365 — one row per kit. A kit is an enrollment bound to one of the site's
 * assets; its health is the gateway heartbeat that carries the `ic3` block; its
 * last count is the newest run pushed under that asset. Pure join, so the page
 * and the tests agree on what a row is.
 */
export interface KitRow {
  hardwareId: string;
  enrollment: GatewayEnrollment;
  asset: Asset | null;
  gateway: GatewayStatus | null;
  lastRun: InventoryRunSummary | null;
}

export const GATEWAY_STALE_MS = 60_000;

export function buildKitRows(
  enrollments: GatewayEnrollment[], assets: Asset[], gateways: Record<string, GatewayStatus>,
  runs: InventoryRunSummary[], warehouseId?: string,
): KitRow[] {
  const byAssetId = new Map(assets.map((a) => [a.id, a]));
  const gws = Object.values(gateways);
  const rows: KitRow[] = [];
  for (const e of enrollments) {
    const asset = e.assetId ? byAssetId.get(e.assetId) ?? null : null;
    // Only this site's kits: a pending enrollment (no asset yet) still belongs here —
    // it is the one the administrator has to approve — unless a warehouse filter is on.
    if (e.assetId && !asset) continue;
    if (warehouseId && asset && asset.warehouseId && asset.warehouseId !== warehouseId) continue;
    const gateway = gws.find((g) => (e.assetId && g.assetId === e.assetId) || (e.gatewayId && g.id === e.gatewayId)) ?? null;
    const lastRun = e.assetId
      ? runs.filter((r) => r.assetId === e.assetId)
          .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0] ?? null
      : null;
    rows.push({ hardwareId: e.hardwareId, enrollment: e, asset, gateway, lastRun });
  }
  return rows.sort((a, b) => (a.asset?.label ?? a.hardwareId).localeCompare(b.asset?.label ?? b.hardwareId));
}

export type KitOnline = "online" | "stale" | "unknown";
export function kitOnline(gateway: GatewayStatus | null, nowMs: number): KitOnline {
  if (!gateway) return "unknown";
  return gateway.online && nowMs - gateway.lastSeenMs <= GATEWAY_STALE_MS ? "online" : "stale";
}

/** "3 min", "2 h", "1 d" — coarse, for a queue age or a last-seen. */
export function coarseAge(ms: number, locale: string): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 90) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m} min`;
  const h = Math.round(m / 60);
  if (h < 36) return `${h} h`;
  return `${Math.round(h / 24).toLocaleString(locale)} d`;
}
